import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { COLLECTIONS } from '../constants/constants';
import { syncMyClaimsFromProfile } from './authUtils';

/**
 * JWT / AuthContext claims — aligned with NavBar and backend verifyReceiptsAccess.
 * @param {object} claims
 * @returns {boolean}
 */
export function hasReceiptsClaims(claims = {}) {
  return (
    claims.admin === true ||
    claims.operations === true ||
    claims.role === 'admin' ||
    claims.role === 'operations'
  );
}

/**
 * @param {object|null} contextUser AuthContext user with .claims
 * @returns {boolean}
 */
export function hasReceiptsAccessFromContextUser(contextUser) {
  if (!contextUser) {
    return false;
  }
  return (
    hasReceiptsClaims(contextUser.claims || {}) ||
    contextUser.isAdmin === true ||
    contextUser.isOperations === true
  );
}

/**
 * @param {object|null} contextUser
 * @returns {boolean}
 */
export function hasReceiptsAdminFromContextUser(contextUser) {
  if (!contextUser) {
    return false;
  }
  const claims = contextUser.claims || {};
  return (
    claims.admin === true ||
    claims.role === 'admin' ||
    contextUser.isAdmin === true
  );
}

/**
 * Whether the user may access Receipts (matches backend verifyReceiptsAccess).
 * @param {import('firebase/auth').User} firebaseUser
 * @returns {Promise<boolean>}
 */
export async function checkReceiptsAccess(firebaseUser) {
  if (!firebaseUser?.uid) {
    return false;
  }

  let tokenResult = await firebaseUser.getIdTokenResult(false);
  if (hasReceiptsClaims(tokenResult.claims || {})) {
    return true;
  }

  try {
    const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, firebaseUser.uid));
    const role = userDoc.data()?.role;
    if (role === 'admin' || role === 'operations') {
      return true;
    }
  } catch {
    // Ignore Firestore errors; try refreshed token below
  }

  tokenResult = await firebaseUser.getIdTokenResult(true);
  if (hasReceiptsClaims(tokenResult.claims || {})) {
    return true;
  }

  try {
    const syncResult = await syncMyClaimsFromProfile(firebaseUser);
    if (syncResult?.success) {
      tokenResult = await firebaseUser.getIdTokenResult(true);
      if (hasReceiptsClaims(tokenResult.claims || {})) {
        return true;
      }
    }
  } catch {
    // Fall through to final denial
  }

  return false;
}

/**
 * @param {import('firebase/auth').User} firebaseUser
 * @returns {Promise<boolean>}
 */
export async function checkReceiptsAdminAccess(firebaseUser) {
  if (!firebaseUser?.uid) {
    return false;
  }

  let tokenResult = await firebaseUser.getIdTokenResult(false);
  const claims = tokenResult.claims || {};
  if (claims.admin === true || claims.role === 'admin') {
    return true;
  }

  try {
    const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, firebaseUser.uid));
    if (userDoc.data()?.role === 'admin') {
      return true;
    }
  } catch {
    // Fall through
  }

  tokenResult = await firebaseUser.getIdTokenResult(true);
  const refreshed = tokenResult.claims || {};
  return refreshed.admin === true || refreshed.role === 'admin';
}

/** Sync nav check from JWT claims. */
export function canShowReceiptsInNav(claims = {}) {
  return hasReceiptsClaims(claims);
}
